<script setup lang="ts">

import { computed } from "vue";
import { isNullOrUndefined } from "is-what";
import type { IMarketplaceViewModel } from "../../Types/IMarketplaceViewModel"
import { ListStyle } from "@app/MyMarketplace/Types/ListStyle";
import { MarketplaceFilterType, type IOfferTile } from "@domain/Marketplace";

import StackedOneColumn from "./StackedOneColumn.vue";
import StackedTwoColumn from "./StackedTwoColumn.vue";
import HorizontalScroll from "./HorizontalScroll.vue";

type Props = { 
    title?: string, 
    subTitle?: string,
    list: IOfferTile[], 
    enableFilter?: boolean,
    listStyle?: ListStyle 
};
const model = defineModel<IMarketplaceViewModel>( { required: true } );
const { title, list, enableFilter = false, listStyle = ListStyle.StackedTwoColumn } = defineProps<Props>();

// Gets a filtered view over the list items
const filteredList = computed( () => 
{

    // No filter is applied
    if( enableFilter == false || model.value.hasFilter == false ) 
    {
        return list;
    }
    
    const cardLinked = model.value.cardLinkedOnlyEnabled;
    const filter = model.value.filterType;

    // If using the instore only filter, we need to order them by shortest to furthest distance.
    if ( filter == MarketplaceFilterType.InStore )
    {
        return list.filter( o => o.isInstoreOffer ).sort( ( l, r ) =>
        {
            if ( !isNullOrUndefined( l.offerDistance ) && !isNullOrUndefined( r.offerDistance ) )
            {
                return l.offerDistance - r.offerDistance;
            }
            
            if ( !isNullOrUndefined( r.offerDistance ) )
            {
                return 1;
            }
            
            if ( !isNullOrUndefined( l.offerDistance ) )
            {
                return -1;
            }

            return 0;
        } );
    }

    return list.filter( o => 
    {
        return ( cardLinked == false || ( cardLinked && o.isCardLinked ) )
            && ( filter == MarketplaceFilterType.All
            || ( filter == MarketplaceFilterType.InApp && o.isInstoreOffer == false )
        )
    });

});

</script>

<template>

    <div class="apple-fix w-full min-h-full">

        <div v-if="model.filteredSubCategory"
            class="flex items-center gap-2 pb-6 px-4 3xl:px-6">
            <span>Exploring <span class="hidden lg:inline">offers in </span></span>
            <button class="flex gap-2 rounded-md items-center py-2 px-3 bg-gray-850 !border border-gray-800"
                    @click="() => model.reset()">
                <span class="text-secondary-400 font-bold">
                    {{ model.filteredSubCategory.displayName }}
                </span>
                <Icon name="cross" class="text-xl"/>
            </button>
        </div>

        <template v-if="listStyle == ListStyle.StackedOneColumn">
            <StackedOneColumn v-model="filteredList" 
                              :total-count="model.totalCount"
                              :unfiltered-count="list.length"
                              :groups="model.activeSubCategories" 
                              :title="title"
                              :sub-title="subTitle"
                              @load-more="() => model.loadOffers( model.activeGroup, model.activeCategory, true )" />
        </template>

        <template v-else-if="listStyle == ListStyle.StackedTwoColumn">
            <StackedTwoColumn v-model="filteredList" 
                              :total-count="model.totalCount"
                              :unfiltered-count="list.length"
                              :groups="model.activeSubCategories" 
                              :title="title"
                              :sub-title="subTitle" />
        </template>

        <template v-else>
            <HorizontalScroll v-model="filteredList" 
                              :total-count="model.totalCount"
                              :unfiltered-count="list.length"
                              :title="title"
                              :sub-title="subTitle" />
        </template>

    </div>
    
</template>
